import styled from 'styled-components';

import senglarGif from '../../assets/images/senglar.gif';

/**
 * Standardized Page Title (H1)
 * - Responsive font size (clamp)
 * - Consistent color (using CSS variable --color-primary or fallback)
 * - Standardized margins for spacing
 */
export const PageTitle = styled.h1`
	font-size: clamp(1.75rem, 5vw, 2.25rem);
	color: var(--color-primary, #2c3e50);
	margin: 0 0 32px 0; 
	padding-top: 24px; 
	text-align: left;
	width: 100%;
	line-height: 1.3;

	& + p,
	& + div,
	& + ul,
	& + ol {
		margin-top: 16px;
	}

	@media (max-width: 768px) {
		margin-bottom: 24px;
		padding-top: 16px;
	}
`;

/**
 * Standardized Section Title (H2)
 * - Responsive font size (clamp)
 * - Consistent color (using CSS variable --color-secondary or fallback)
 * - Standardized margins for spacing
 */
export const SectionTitle = styled.h2`
	font-size: clamp(1.5rem, 4vw, 1.75rem);
	color: var(--color-secondary, #34495e);
	margin: 0 0 16px 0; 
	padding-top: 32px; 
	text-align: left;
	width: 100%;
	line-height: 1.4;

	& + p,
	& + div,
	& + ul,
	& + ol {
		margin-top: 8px;
	}

	@media (max-width: 768px) {
		margin-bottom: 12px;
		padding-top: 24px;
	}
`;

/**
 * Standardized Sub-Section Title (H3) - Less common, but useful
 * - Responsive font size (clamp)
 * - Consistent color
 * - Standardized margins
 */
export const SubSectionTitle = styled.h3<{
	$align: 'left' | 'center';
}>`
	font-size: clamp(1.25rem, 3.5vw, 1.5rem);
	color: var(--color-text-secondary, #4a4a4a);
	margin: 0 0 12px 0; 
	padding-top: 24px; 
	text-align: ${(props) => props.$align || 'left'};
	width: 100%;
	line-height: 1.4;

	& + p,
	& + div,
	& + ul,
	& + ol {
		margin-top: 6px;
	}

	@media (max-width: 768px) {
		margin-bottom: 8px;
		padding-top: 16px;
	}
`;

/**
 * Standard Content Container
 * - Consistent max-width for readable content
 * - Centering using margin: auto
 * - Responsive padding
 */
export const StandardContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%; 
	max-width: 900px; 
	margin: 0 auto; 
	padding: 40px 20px; 

	@media (max-width: 768px) {
		padding: 20px 15px; 
	}
`;

/**
 * Wider Content Container (e.g., for grids)
 * - Wider max-width
 * - Centering using margin: auto
 * - Responsive padding
 */
export const WideContentContainer = styled(StandardContentContainer)`
	max-width: 1200px; 
`;

/**
 * Basic Paragraph Styling
 */
export const Paragraph = styled.p`
	font-size: clamp(1rem, 1.8vw, 1.1rem);
	line-height: 1.6;
	color: var(--color-text-primary, #333);
	margin-bottom: 1rem; 
	text-align: justify;

	@media (max-width: 768px) {
		text-align: left; 
	}
`;

/**
 * Consistent Loading Message Container
 * - Centers its children (e.g., a loading spinner with text)
 */
export const LoadingMessage = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding: 40px;
	text-align: center;
	font-size: 1.2rem;
	width: 100%;
`;

/**
 * Consistent Error Message Container
 * - Extends LoadingMessage but with red text
 */
export const ErrorMessage = styled(LoadingMessage)`
	color: red;
`;

/**
 * Full Page Loader Container
 * - Covers the entire viewport
 * - Centers its children (e.g., a loading spinner)
 * - Has a semi-transparent background
 */
export const FullPageLoader = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: rgba(255, 255, 255, 0.8);
	z-index: 9999;
`;

/**
 * Simple Loading GIF Component
 * - No text, no extra animations, just the GIF.
 * - Accepts a size prop.
 */
const gifSizes = {
	small: '48px',
	medium: '96px',
	large: '200px',
};

export const LoadingGif = styled.img.attrs<{
	size?: keyof typeof gifSizes;
}>((props) => ({
	src: senglarGif,
	alt: 'Carregant...',
	width: gifSizes[props.size || 'medium'],
	height: gifSizes[props.size || 'medium'],
}))<{ size?: keyof typeof gifSizes }>`

	box-sizing: border-box;
	padding: calc(${(props) => gifSizes[props.size || 'medium']} * 0.2925);
	object-fit: contain;
	width: ${(props) => gifSizes[props.size || 'medium']};
	height: ${(props) => gifSizes[props.size || 'medium']};
`;

export const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;

    th, td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
    }

    th {
        background-color: #f2f2f2;
    }
`;

/**
 * Sticky Pagination Container with FLIP animation support
 */
export const StickyFlipPaginationContainer = styled.div<{
	$navHeight: number;
	$isBottom?: boolean;
	$isStuck?: boolean;
}>`
	${({ $isBottom, $navHeight }) =>
		$isBottom
			? `
    position: relative;
    order: 10;
    margin-top: 32px;
    margin-bottom: 32px;
    width: 100%;
    z-index: 9;
  `
			: `
    position: sticky;
    top: ${$navHeight + 10}px;
    z-index: 10;
    width: 100%;
    background: transparent;
    padding: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
    margin-bottom: 32px;
  `}
	transform: ${({ $isStuck }) => ($isStuck ? 'scale(1.06)' : 'scale(1)')};
	transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1);
`;

/**
 * Content Wrapper for scroll detection and layout
 */
export const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;